import React from 'react';

type Series = { label: string; color: string; values: Array<number | null> };

export const ActivityChart: React.FC<{ labels: string[]; series: Series[]; ariaLabel?: string; maximum?: number; valueSuffix?: string }> = ({ labels, series, ariaLabel = "Analytics activity chart", maximum, valueSuffix = "" }) => {
  const width = 760;
  const height = 260;
  const left = 38;
  const right = 16;
  const top = 18;
  const bottom = 38;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const max = maximum ?? Math.max(1, ...series.flatMap((item) => item.values).filter((value): value is number => value !== null));
  const x = (index: number) => left + (labels.length <= 1 ? plotWidth / 2 : index * plotWidth / (labels.length - 1));
  const y = (value: number) => top + plotHeight - value / max * plotHeight;
  const labelEvery = Math.max(1, Math.ceil(labels.length / 7));

  return (
    <div>
      <div className="flex flex-wrap gap-4 pb-3 text-xs font-medium text-gray-600">
        {series.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="min-w-[620px] w-full" role="img" aria-label={ariaLabel}>
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const gridY = top + plotHeight * fraction;
            const value = Math.round(max * (1 - fraction));
            return (
              <g key={fraction}>
                <line x1={left} x2={width - right} y1={gridY} y2={gridY} stroke="#e5e7eb" />
                <text x={left - 7} y={gridY + 4} textAnchor="end" fontSize="10" fill="#6b7280">{value}{valueSuffix}</text>
              </g>
            );
          })}
          {series.map((item) => {
            const path = item.values.map((value, index) => value === null ? ''
              : `${index === 0 || item.values[index - 1] === null ? 'M' : 'L'} ${x(index)} ${y(value)}`).join(' ');
            return <g key={item.label}>
              <path d={path} fill="none" stroke={item.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {item.values.map((value, index) => value !== null
                && (index === 0 || item.values[index - 1] === null)
                && (index === item.values.length - 1 || item.values[index + 1] === null)
                ? <circle key={index} cx={x(index)} cy={y(value)} r="4" fill={item.color} /> : null)}
            </g>;
          })}
          {labels.map((label, index) => index % labelEvery === 0 || index === labels.length - 1 ? (
            <text key={`${label}-${index}`} x={x(index)} y={height - 12} textAnchor="middle" fontSize="10" fill="#6b7280">
              {new Date(`${label}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </text>
          ) : null)}
        </svg>
      </div>
    </div>
  );
};
